import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { SpadeMark } from "@/components/brand";

const LINKS = [
  { name: "The Festival", path: "/about", testId: "nav-link-about" },
  { name: "Events", path: "/events", testId: "nav-link-events" },
  { name: "Schedule", path: "/schedule", testId: "nav-link-schedule" },
  { name: "Gallery", path: "/gallery", testId: "nav-link-gallery" },
  { name: "Sponsors", path: "/sponsors", testId: "nav-link-sponsors" },
  { name: "Contact", path: "/contact", testId: "nav-link-contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header data-testid="site-navbar" className="sticky top-0 z-50 backdrop-blur-xl bg-[#08080A]/85 border-b border-[#D4AF37]/20">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8 py-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <SpadeMark className="h-7 w-7 transition-transform duration-500 group-hover:rotate-[20deg]" />
          <span className="font-bebas text-2xl tracking-[0.22em] text-[#F6F4EE]">
            FINATEX<span className="text-[#D4AF37]">'26</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              data-testid={link.testId}
              className={({ isActive }) =>
                `text-[11px] uppercase tracking-[0.22em] font-medium transition-colors duration-300 ${
                  isActive ? "text-[#D4AF37]" : "text-[#A19E95] hover:text-[#F6F4EE]"
                }`
              }
            >
              {link.name}
            </NavLink>
          ))}
          <Link
            to="/register"
            data-testid="nav-link-register"
            className="border border-[#D4AF37]/60 px-5 py-2 text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] transition-all duration-300 hover:bg-[#D4AF37] hover:text-[#08080A]"
          >
            Register
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-[#F6F4EE] p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key={location.pathname}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden border-t border-[#D4AF37]/15 bg-[#0B0B0F]"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {LINKS.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-${link.testId}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `text-sm uppercase tracking-[0.22em] ${isActive ? "text-[#D4AF37]" : "text-[#A19E95]"}`
                  }
                >
                  {link.name}
                </NavLink>
              ))}
              <Link
                to="/register"
                data-testid="mobile-nav-link-register"
                onClick={() => setOpen(false)}
                className="mt-2 border border-[#D4AF37]/60 px-5 py-3 text-center text-xs uppercase tracking-[0.22em] text-[#D4AF37]"
              >
                Register
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
